export type LoginRequest = {
  email: string;
  password: string;
};

/** Returned when credentials are valid but MFA is required before a JWT is issued. */
export type MfaRequiredResponse = {
  status: "MFA_REQUIRED";
  challengeToken: string;
  /**
   * Which methods the user can choose from.
   * "EMAIL" is always present. "TOTP" is included only when an authenticator
   * app has been configured (totpConfigured = true).
   */
  availableMethods: ("EMAIL" | "TOTP")[];
  /** Whether the user has a confirmed TOTP secret (authenticator app registered). */
  totpConfigured: boolean;
  /** Masked email shown in the UI, e.g. "j***@eatta.org". */
  emailMasked?: string | null;
};

/** Returned on a successful fully-authenticated login (no MFA, or after MFA verify). */
export type AuthTokenResponse = {
  tokenType: string;
  accessToken: string;
  expiresAt: string;
  username: string;
  email: string;
  roles: string[];
  permissions: string[];
  /** Never "MFA_REQUIRED" — that shape is MfaRequiredResponse. */
  status: "SUCCESS" | "AUTHENTICATED" | "PASSWORD_CHANGE_REQUIRED" | (string & Record<never, never>);
};

/** Type guard — narrows LoginResponse to MfaRequiredResponse. */
export function isMfaRequired(r: LoginResponse): r is MfaRequiredResponse {
  return r.status === "MFA_REQUIRED";
}

/** Login may return a full JWT or an MFA challenge. */
export type LoginResponse = AuthTokenResponse | MfaRequiredResponse;

export type MfaVerifyRequest = {
  challengeToken: string;
  code: string;
  /** Which method the user chose — tells the backend whether to validate against email OTP or TOTP. */
  method: "EMAIL" | "TOTP";
};

/** Request body for POST /api/auth/mfa/challenge/email */
export type SendEmailChallengeRequest = {
  challengeToken: string;
};

/** Response from POST /api/auth/mfa/challenge/email */
export type SendEmailChallengeResponse = {
  sent: boolean;
  maskedEmail: string;
};

export type MfaStatusResponse = {
  /** MFA is active for this account. */
  enabled: boolean;
  /** Which method is currently active at login. null when MFA is disabled. */
  type: "TOTP" | "EMAIL" | null;
  /** A TOTP secret has been confirmed — authenticator app is registered. */
  totpConfigured: boolean;
  /** totp/init was called but totp/confirm has not been completed. */
  setupPending: boolean;
};

export type TotpInitResponse = {
  qrCodeDataUrl: string;
  secret: string;
};

export type TotpConfirmRequest = {
  code: string;
};

export type MfaDisableRequest = {
  password: string;
};

export type CurrentUserResponse = {
  id: number;
  username: string;
  email: string;
  roles: string[];
  permissions: string[];
  jobTitle?: string | null;
  mustChangePassword: boolean;
  enabled: boolean;
  locked: boolean;
};

export type PasswordResetRequest = {
  email: string;
};

export type PasswordResetCompleteRequest = {
  token: string;
  otp: string;
  newPassword: string;

  
};
export type PasswordChallengeResponse = {

  email: string;

  maskedEmail?: string | null;

  expiresAt?: string | null;

  purpose: string;

  sent: boolean;

};
export type ChangePasswordRequest = {

  currentPassword: string;

  newPassword: string;

  confirmPassword: string;

};
export type User = {
  id: number;
  username: string;
  email: string;

  enabled: boolean;
  locked: boolean;
  mustChangePassword?: boolean;

  roles: string[];
  permissions?: string[];

  lastLoginAt?: string | null;
  createdAt?: string | null;

  employeeId?: number | null; // 🔗 link to employee
};